import { Position } from "@/services/positionService";
import { PositionFields } from "./PositionFields";
import { BidFormValues } from "./types";
import { useFormContext } from "react-hook-form";
import { axiosInstance } from "@/pages/auth/auth.context";
import { useEffect, useState } from "react";

interface BidPositionsSectionProps {
  positions: Position[];
  eventId?: string;
}

export interface PositionData extends Position {
  quantity?: number;
}

interface EventPosition {
  id: string;
  tableName: string;
  employeesRequired: number;
  billPrice: number;
  employeePay: number;
  quantity: number;
}

export function BidPositionsSection({
  positions,
  eventId,
}: BidPositionsSectionProps) {
  const [mergedPositions, setMergedPositions] = useState<PositionData[]>([]);
  const { setValue } = useFormContext<BidFormValues>();

  useEffect(() => {
    const fetchEventPositions = async () => {
      if (!eventId || eventId === "new") {
        // For new events, use the default positions
        setMergedPositions(positions.map((pos) => ({ ...pos })));
        return;
      }

      try {
        // Fetch event-specific positions
        const route = `${process.env.REACT_APP_BASE_API_URL}event/${eventId}/positions`;
        const response = await axiosInstance.get<EventPosition[]>(route);
        const eventPositions = response.data;

        // Create a map of positions with quantities from event positions
        const positionMap = new Map(
          eventPositions.map((ep: EventPosition) => [
            ep.tableName,
            {
              ...ep,
              quantity: ep.quantity,
            },
          ]),
        );

        // Merge with default positions, preferring event-specific data
        const merged = positions.map((pos) => {
          const eventPosition = positionMap.get(pos.tableName);
          if (eventPosition) {
            // Set the form value for this position
            setValue(`positions.${pos.id}.quantity`, eventPosition.quantity);
            return { ...pos };
          }
          return { ...pos };
        });
        setMergedPositions(merged);
      } catch (error) {
        console.error("Error fetching event positions:", error);
        // Fallback to default positions if fetch fails
        setMergedPositions(positions.map((pos) => ({ ...pos })));
      }
    };

    fetchEventPositions();
  }, [positions, eventId, setValue]);

  return (
    <div className="space-y-4">
      <PositionFields positions={mergedPositions} />
    </div>
  );
}
